import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getHospitals, getPharmacies, Hospital, Pharmacy } from '../../../api/branchesApi';
import { RootState } from '../../store';

// State interface
interface HealthState {
  hospitals: {
    data: Hospital[];
    loading: boolean;
    error: string | null;
  };
  pharmacies: {
    data: Pharmacy[];
    loading: boolean;
    error: string | null;
  };
}


const initialState: HealthState = {
  hospitals: {
    data: [],
    loading: false,
    error: null,
  },
  pharmacies: {
    data: [],
    loading: false,
    error: null,
  },
};


export const fetchHospitals = createAsyncThunk(
  'health/fetchHospitals',
  async (language: 'az' | 'en' | 'ru' = 'az', { rejectWithValue }) => {
    try {
      const result = await getHospitals();

      if (result.status === 200 && 'results' in result.data) {
        return result.data.results;
      }

      if ('error' in result.data) {
        return rejectWithValue(result.data.error || 'Xəstəxanalar yüklənərkən xəta baş verdi');
      }

      return rejectWithValue('Gözlənilməz xəta baş verdi');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Xəta baş verdi');
    }
  }
);

export const fetchPharmacies = createAsyncThunk(
  'health/fetchPharmacies',
  async (language: 'az' | 'en' | 'ru' = 'az', { rejectWithValue }) => {
    try {
      const result = await getPharmacies();

      if (result.status === 200 && 'results' in result.data) {
        return result.data.results;
      }

      if ('error' in result.data) {
        return rejectWithValue(result.data.error || 'Apteklər yüklənərkən xəta baş verdi');
      }

      return rejectWithValue('Gözlənilməz xəta baş verdi');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Xəta baş verdi');
    }
  }
);

export const fetchAllHealthLocations = createAsyncThunk(
  'health/fetchAllHealthLocations',
  async (language: 'az' | 'en' | 'ru' = 'az', { dispatch }) => {
    await Promise.all([
      dispatch(fetchHospitals(language)),
      dispatch(fetchPharmacies(language))
    ]);
  }
);

// Slice
const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    // Clear all data
    clearHealthData: (state) => {
      state.hospitals = initialState.hospitals;
      state.pharmacies = initialState.pharmacies;
    },
    // Clear errors
    clearHospitalsError: (state) => {
      state.hospitals.error = null;
    },
    clearPharmaciesError: (state) => {
      state.pharmacies.error = null;
    },
  },
  extraReducers: (builder) => {
    // Hospitals
    builder
      .addCase(fetchHospitals.pending, (state) => {
        state.hospitals.loading = true;
        state.hospitals.error = null;
      })
      .addCase(fetchHospitals.fulfilled, (state, action: PayloadAction<Hospital[]>) => {
        state.hospitals.loading = false;
        state.hospitals.data = action.payload;
        state.hospitals.error = null;
      })
      .addCase(fetchHospitals.rejected, (state, action) => {
        state.hospitals.loading = false;
        state.hospitals.error = action.payload as string || 'Xəta baş verdi';
      });

    // Pharmacies
    builder
      .addCase(fetchPharmacies.pending, (state) => {
        state.pharmacies.loading = true;
        state.pharmacies.error = null;
      })
      .addCase(fetchPharmacies.fulfilled, (state, action: PayloadAction<Pharmacy[]>) => {
        state.pharmacies.loading = false;
        state.pharmacies.data = action.payload;
        state.pharmacies.error = null;
      })
      .addCase(fetchPharmacies.rejected, (state, action) => {
        state.pharmacies.loading = false;
        state.pharmacies.error = action.payload as string || 'Xəta baş verdi';
      });
  },
});

// Actions
export const { clearHealthData, clearHospitalsError, clearPharmaciesError } = healthSlice.actions;

export const selectHospitals = (state: RootState) => state.branches.hospitals.data;
export const selectHospitalsLoading = (state: RootState) => state.branches.hospitals.loading;
export const selectHospitalsError = (state: RootState) => state.branches.hospitals.error;

export const selectPharmacies = (state: RootState) => state.branches.pharmacies.data;
export const selectPharmaciesLoading = (state: RootState) => state.branches.pharmacies.loading;
export const selectPharmaciesError = (state: RootState) => state.branches.pharmacies.error;

export const selectIsAnyLoading = (state: RootState) =>
  state.branches.hospitals.loading || state.branches.pharmacies.loading;

export const selectHasAnyError = (state: RootState) =>
  state.branches.hospitals.error !== null || state.branches.pharmacies.error !== null;

export default healthSlice.reducer;